import {
  ActionSheet,
  Alert,
  Button,
  CardPicasso,
  FormInput,
  FormInputCheckbox,
  Prose,
} from "@liberetech/design-system";
import HeroFull, { imageSizes } from "@liberetech/design-system/HeroFull";
import Link from "@liberetech/design-system/Link";
import Paper from "@liberetech/design-system/Paper/Paper";
import Section from "@liberetech/design-system/Section";
import SectionHeader from "@liberetech/design-system/SectionHeader";
import Tag from "@liberetech/design-system/Tag";
import classNames from "classnames";
import { nanoid } from "nanoid";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import Carousel from "components/Carousel/Carousel";
import ImageCDN from "components/ImageCDN/ImageCDN";
import Map, { MapMarker } from "components/Map/Map";
import NextLinkButton from "components/NextLinkButton/NextLinkButton";
import SectionFAQ from "components/SectionFAQs/SectionFAQs";
import SectionSpecialRates from "components/SectionSpecialRates/SectionSpecialRates";
import SectionStickyNav from "components/SectionStickyNav/SectionStickyNav";
import { FAQPageJsonLd, LanguageAlternates, Seo } from "components/Seo/Seo";
import { assetOwnerInfo, isManagement } from "lib/asset/operatorModel";
import fetchCms from "lib/client/cms";
import customerIoClient from "lib/client/customerIo";
import { ssrFetcher } from "lib/client/graphql";
import serverSideTranslations from "lib/i18n/_pagesRouterCompat/serverSideTranslations";
import { Trans, useLanguage, useTranslation } from "lib/i18n/client";
import { useTranslation as useTranslationServer } from "lib/i18n/server";
import { captureError } from "lib/logger";
import useForm from "lib/useForm";
import { email } from "lib/validations";
import withDirectChannelCommonProps from "lib/withDirectChannelCommonProps";
import SectionAmenities from "pagesComponents/_shared/SectionAmenities/SectionAmenities";
import SectionReviews from "pagesComponents/_shared/SectionReviews/SectionReviews";
import SectionWithOverlappedImages from "pagesComponents/_shared/SectionWithOverlappedImages/SectionWithOverlappedImages";
import {
  IconAirConditioning,
  IconAirport,
  IconBarRestaurant,
  IconBike,
  IconBus,
  IconCar,
  IconCityCenter,
  IconCommonTerrace,
  IconEarlyCheckin,
  IconEquippedKitchen,
  IconExpressCheckIn,
  IconHairDryer,
  IconLateCheckout,
  IconLaundryIroning,
  IconLocker,
  IconLuggageLockers,
  IconLuggageStorage,
  IconMapDefaultMarker,
  IconMarker,
  IconMetro,
  IconMobileKey,
  IconOnlineReception,
  IconParking,
  IconPets,
  IconReducedMobility,
  IconSwimmingPool,
  IconTaxi,
  IconTowels,
  IconTrain,
  IconTram,
  IconTV,
  IconWiFi,
} from "pagesComponents/asset/index/AssetHomeIcon/AssetHomeIcon";
import AssetHomeIntro from "pagesComponents/asset/index/AssetHomeIntro/AssetHomeIntro";
import AssetSectionSpaceCategories from "pagesComponents/asset/index/AssetSectionSpaceCategories/AssetSectionSpaceCategories";
import {
  AssetIndexDocument,
  AssetIndexQuery,
  AssetIndexQueryVariables,
} from "pagesComponents/asset/index/index.generated";
import { Brand, DirectChannelCommonProps } from "types";

import styles from "./index.module.css";

const Asset: React.FC<AssetProps & DirectChannelCommonProps> = ({
  asset,
  assetGQL,
  isViewerInCommunity,
  brand,
  mapsApiKey,
}) => {
  const { t } = useTranslation("asset");
  const { query, locale } = useRouter();

  const [isMidStayModalVisible, setMidStayModalVisible] = useState<boolean>(
    !!query.showMidStayForm,
  );
  const {
    isVisible: isCarouselVisible,
    open: openCarousel,
    close: closeCarousel,
  } = useCarousel();

  const {
    title,
    fullName,
    featuredImage,
    content,
    destination,
    properties: {
      claim,
      features: {
        location,
        bikeParking,
        privateParking,
        reducedMobility,
        offlineReception,
        laundryAndIroning,
        swimmingPool,
        lockers,
        kitchen,
        tv,
        barRestaurant,
        barRestaurantOpening,
        luggageStorage,
        luggageLockers,
        hairDryer,
        commonTerrace,
      },
      location: { address, city, postalCode, province, country },
      howToArrive: { car, airport, bus, metro, taxi, tram, train },
      faq,
      travelGuide,
      gallery,
      geographicPosition: { latitude, longitude },
      registrationNumber,
    },
    seo,
  } = asset;

  const ownerInfo = isManagement(asset.properties.id)
    ? assetOwnerInfo(asset.properties.id)
    : null;
  const hasReviews = (assetGQL?.reviews?.nodes || []).length > 0;
  const hasTravelGuide =
    (brand.enabledFeatures?.includes("destinations") &&
      destination.destination) ||
    ((!brand.enabledFeatures?.includes("destinations") ||
      !destination.destination) &&
      travelGuide);
  const hasSpecialRatesSection = brand.id === "libere";

  return (
    <>
      <Seo
        title={t("seoTitle", {
          assetFullName: fullName,
          brand: brand.name,
        })}
        description={seo.metaDesc}
        titleTemplate="%s"
      />
      <HeroFull
        className={styles.hero}
        title={fullName}
        subtitle={claim}
        claim={
          <>
            <svg viewBox="0 0 24 24">
              <path d="M3.75 12h.75c1.701 0 3.62.935 4.5 2.25h3c1.505 0 3 1.495 3 3H8.25" />
              <path d="M14.52 15.75h4.98c2.719 0 3 1.5 3 1.5l-10.74 4.362a3.22 3.22 0 0 1-2.638-.11L3.75 18.774" />
              <path d="M18 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.75 10.5h-3v9.75h3V10.5ZM9 7.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
            </svg>
            <span>
              <Trans
                i18nKey={"common:hero.bestPrice"}
                components={[<strong />]}
              />
            </span>
          </>
        }
        image={
          <div className={styles.heroImageWrap}>
            <ImageCDN
              src={featuredImage?.node.sourceUrl || ""}
              alt={title}
              sizes={imageSizes}
            />
            {gallery && gallery.length > 0 && (
              <button
                onClick={() => {
                  openCarousel();
                }}
                className={styles.heroImageGalleryButton}
              >
                <Tag
                  icon={
                    <svg viewBox="0 0 32 32">
                      <path d="M14.858 21.142 22 14l9 8M1 25l9-8 8.501 7.249" />
                      <path d="M31 3H1v26h30V3Z" />
                      <path d="M14.5 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                    </svg>
                  }
                >
                  {t("gallery")}
                </Tag>
              </button>
            )}
          </div>
        }
      />
      {isCarouselVisible && (
        <div className={styles.carousel}>
          <Carousel
            classNames={{
              images: styles.carouselImages,
              buttons: styles.carouselButtons,
              button: styles.carouselButton,
            }}
          >
            {gallery.map((image, i) => (
              <ImageCDN key={i} src={image.sourceUrl} alt="" sizes="100vw" />
            ))}
          </Carousel>
          <button
            className={styles.carouselCloseButton}
            onClick={() => {
              closeCarousel();
            }}
          >
            <svg viewBox="0 0 16 16">
              <path d="m13.5 2.5-11 11M13.5 13.5l-11-11" />
            </svg>
          </button>
        </div>
      )}
      <AssetHomeIntro
        asset={assetGQL}
        content={content}
        offlineReception={offlineReception}
      />
      <SectionStickyNav
        sections={[
          { id: "spaces", title: t("stickyNav.spaces") },
          ...(hasReviews
            ? [{ id: "reviews", title: t("stickyNav.reviews") }]
            : []),
          { id: "location", title: t("stickyNav.location") },
          { id: "amenities", title: t("stickyNav.amenities") },
          ...(hasSpecialRatesSection
            ? [{ id: "special-rates", title: t("stickyNav.specialRates") }]
            : []),
          ...(faq ? [{ id: "faq", title: t("stickyNav.faq") }] : []),
          ...(hasTravelGuide
            ? [{ id: "travel-guide", title: t("stickyNav.travelGuide") }]
            : []),
        ]}
      >
        <AssetSectionSpaceCategories
          title={title}
          asset={asset}
          assetGQL={assetGQL}
          isViewerInCommunity={isViewerInCommunity}
          onContactClicked={() => {
            setMidStayModalVisible(true);
          }}
        />
        {hasReviews && assetGQL && (
          <SectionReviews
            id="reviews"
            label={title}
            title={t("reviews.title", { asset: title })}
            reviews={assetGQL.reviews?.nodes}
            averageRating={assetGQL.reviews?.averageRating}
            reviewCount={assetGQL.reviews?.count}
          />
        )}
        <Section className={styles.location} id="location">
          <Map
            className={styles.locationMap}
            location={{ latitude, longitude }}
            apiKey={mapsApiKey}
          >
            <MapMarker
              className={styles.locationMapMarker}
              lat={latitude}
              lng={longitude}
            >
              {brand.design?.marker ? (
                <img src={brand.design.marker} />
              ) : (
                <IconMapDefaultMarker />
              )}
            </MapMarker>
          </Map>
          <div className="grid">
            <Paper className={styles.locationCard} elevation={1}>
              <SectionHeader title={t("locationTitle")} />
              <ul className={styles.locationContact}>
                <li className={styles.locationItem}>
                  <IconMarker />
                  <div>
                    <p>{address}</p>
                    <p>
                      {postalCode} {city} ({province}) {country}
                    </p>
                  </div>
                </li>
              </ul>
              <ul className={styles.locationHowToArrive}>
                {airport && (
                  <li>
                    <HowToArrive
                      icon={<IconAirport />}
                      title={t("howToArrive.airport")}
                    >
                      {airport}
                    </HowToArrive>
                  </li>
                )}
                {car && (
                  <li>
                    <HowToArrive
                      icon={<IconCar />}
                      title={t("howToArrive.car")}
                    >
                      {car}
                    </HowToArrive>
                  </li>
                )}
                {bus && (
                  <li>
                    <HowToArrive
                      icon={<IconBus />}
                      title={t("howToArrive.bus")}
                    >
                      {bus}
                    </HowToArrive>
                  </li>
                )}
                {metro && (
                  <li>
                    <HowToArrive
                      icon={<IconMetro />}
                      title={t("howToArrive.metro")}
                    >
                      {metro}
                    </HowToArrive>
                  </li>
                )}
                {taxi && (
                  <li>
                    <HowToArrive
                      icon={<IconTaxi />}
                      title={t("howToArrive.taxi")}
                    >
                      {taxi}
                    </HowToArrive>
                  </li>
                )}
                {tram && (
                  <li>
                    <HowToArrive
                      icon={<IconTram />}
                      title={t("howToArrive.tram")}
                    >
                      {tram}
                    </HowToArrive>
                  </li>
                )}
                {train && (
                  <li>
                    <HowToArrive
                      icon={<IconTrain />}
                      title={t("howToArrive.train")}
                    >
                      {train}
                    </HowToArrive>
                  </li>
                )}
              </ul>
            </Paper>
          </div>
        </Section>
        <SectionAmenities
          label={title}
          title={t("amenities.title", { assetName: title })}
          description={t("amenities.description")}
          mainAmenitiesTitle={t("amenities.brand", { brand: brand.name })}
          mainAmenities={[
            brand.app &&
              !assetGQL?.access?.isStandalone && {
                icon: <IconMobileKey />,
                title: t("feature.mobileKey"),
              },
            {
              icon: <IconOnlineReception />,
              title: offlineReception
                ? t("feature.offlineReception")
                : t("feature.onlineReception"),
            },
            { icon: <IconWiFi />, title: t("feature.wifi") },
            {
              icon: <IconExpressCheckIn />,
              title: t("feature.expressCheckin"),
            },
            {
              icon: <IconPets />,
              title: t("feature.pets"),
              titleSup: asset.properties.type === "hostel" ? 1 : undefined,
            },
            {
              icon: <IconTowels />,
              title: t("feature.linenAndTowels"),
              titleSup: asset.properties.type === "hostel" ? 2 : undefined,
            },
            {
              icon: <IconAirConditioning />,
              title: t("feature.airConditioning"),
            },
          ].filter((f) => !!f)}
          secondaryAmenities={[
            {
              icon: <IconMarker />,
              title: location,
            },
            {
              icon: <IconCityCenter />,
              title: t("feature.cityCenter"),
            },
            {
              icon: <IconCar />,
              title: t("feature.access"),
            },
            laundryAndIroning && {
              icon: <IconLaundryIroning />,
              title: t("feature.laundryIroning"),
            },
            privateParking && {
              icon: <IconParking />,
              title: t("feature.privateParking"),
            },
            bikeParking && {
              icon: <IconBike />,
              title: t("feature.bikeParking"),
            },
            commonTerrace && {
              icon: <IconCommonTerrace />,
              title: t("feature.commonTerrace"),
            },
            swimmingPool && {
              icon: <IconSwimmingPool />,
              title: t("feature.swimmingPool"),
            },
            lockers &&
              lockers != "no" && {
                icon: <IconLocker />,
                title: t(`feature.lockers.${lockers}`),
              },
            luggageStorage && {
              icon: <IconLuggageStorage />,
              title: t("feature.luggageStorage.title"),
              titleSup: asset.properties.type === "hostel" ? 3 : 1,
              description: t("feature.luggageStorage.description"),
            },
            luggageLockers && {
              icon: <IconLuggageLockers />,
              title: t("feature.luggageLockers.title"),
              titleSup: asset.properties.type === "hostel" ? 3 : 1,
              description: t("feature.luggageLockers.description"),
            },
            {
              icon: <IconEarlyCheckin />,
              title: t("feature.earlyCheckin.title"),
              titleSup: asset.properties.type === "hostel" ? 3 : 1,
              description: t("feature.earlyCheckin.description", {
                time: new Date(`1970-01-01 15:00`).toLocaleTimeString(locale, {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }),
            },
            {
              icon: <IconLateCheckout />,
              title: t("feature.lateCheckout.title"),
              titleSup: asset.properties.type === "hostel" ? 3 : 1,
              description: t("feature.lateCheckout.description", {
                time: new Date("1970-01-01 11:00").toLocaleTimeString(locale, {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }),
            },
            barRestaurant && {
              icon: <IconBarRestaurant />,
              title: t("feature.barRestaurant"),
              description:
                barRestaurantOpening &&
                `${t("feature.barRestaurantOpening", {
                  hours: barRestaurantOpening,
                })}`,
            },
            kitchen &&
              kitchen != "no" && {
                icon: <IconEquippedKitchen />,
                title: t(`feature.kitchen.${kitchen}`),
              },
            tv !== "no" && {
              icon: <IconTV />,
              title: t(
                tv === "common_areas" ? "feature.tvCommonAreas" : "feature.tv",
              ),
            },
            (hairDryer === "yes" || hairDryer === "under_request") && {
              icon: <IconHairDryer />,
              title: t(
                hairDryer === "yes"
                  ? "feature.hairDryer"
                  : "feature.hairDryerUnderRequest",
              ),
            },
            reducedMobility && {
              icon: <IconReducedMobility />,
              title: t("feature.reducedMobility"),
            },
          ].filter((f) => !!f)}
          footnotes={[
            asset.properties.type === "hostel" && {
              text: t("feature.footnotes.petsOnlyPrivateRooms"),
            },
            asset.properties.type === "hostel" && {
              text: t("feature.footnotes.towelRental"),
            },
            {
              text: t("feature.footnotes.accordingToAvailability"),
            },
          ].filter((f) => !!f)}
        />
        {hasSpecialRatesSection && (
          <SectionSpecialRates
            label={title}
            title={t("specialRates.title")}
            description={
              <Trans
                i18nKey="asset:specialRates.description"
                values={{ asset: title }}
                components={{ strong: <strong /> }}
              />
            }
            id="special-rates"
          />
        )}
        {faq && (
          <SectionFAQ
            label={title}
            title={t("faqTitle", { asset: fullName })}
            faqs={faq}
            background={hasSpecialRatesSection ? "contrast" : "default"}
          />
        )}
        {(!brand.enabledFeatures?.includes("destinations") ||
          !destination.destination) &&
          travelGuide && (
            <Section
              background="secondary"
              className={styles.guide}
              id="travel-guide"
            >
              <SectionHeader
                centered
                label={title}
                title={t("travelGuideTitle", { city })}
                className="grid"
              />
              <ul className={classNames(styles.guideGrid, "grid")}>
                {travelGuide.map((guideBlock, i) => (
                  <li key={i} className={styles.guideItem}>
                    <CardPicasso
                      title={guideBlock.title}
                      titleAs="h3"
                      image={
                        <img srcSet={guideBlock.image.srcSet} loading="lazy" />
                      }
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: guideBlock.content }}
                      />
                    </CardPicasso>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        {brand.enabledFeatures?.includes("destinations") &&
          destination.destination && (
            <SectionWithOverlappedImages
              background="secondary"
              id="travel-guide"
              label={title}
              title={t("travelGuideTitle", { city })}
              description={
                <Prose>
                  <p>{t("travelGuideDescription", { city })}</p>
                  <NextLinkButton
                    href={`${destination.destination.uri}/${t(
                      "common:routes.travelGuide",
                    )}`}
                    locale={false}
                  >
                    {t("travelGuideLink")}
                  </NextLinkButton>
                </Prose>
              }
              images={{
                above: destination.destination.about.images.above.srcSet,
                below: destination.destination.about.images.below.srcSet,
              }}
              inverted
            />
          )}
        {registrationNumber && (
          <Section>
            <div className="grid">
              <div>
                {ownerInfo && (
                  <p className={styles.legalInfoTitle}>{t("legal.title")}:</p>
                )}
                <div className={styles.legalInfoItems}>
                  <Tag>{t("registrationNumber", { registrationNumber })}</Tag>
                  {ownerInfo && (
                    <>
                      <Tag>
                        {t("legal.owner", {
                          name: ownerInfo.name,
                          cif: ownerInfo.cif,
                          address: `${ownerInfo.address}, ${ownerInfo.postalCode} ${ownerInfo.city}, ${ownerInfo.province}`,
                        })}
                      </Tag>
                      <Tag>
                        {t("legal.operator", {
                          name: "Libere Hospitality S.L.",
                          cif: "B-95953683",
                          address:
                            "Calle Gran Via Diego Lopez De Haro, 2, 48001 Bilbao, Bizkaia",
                        })}
                      </Tag>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Section>
        )}
      </SectionStickyNav>
      {faq && (
        <FAQPageJsonLd
          mainEntity={faq.map((faqItem) => ({
            questionName: faqItem.question,
            acceptedAnswerText: faqItem.answer.replace(/<\/?[a-z]*>/g, ""),
          }))}
        />
      )}
      <MidStayForm
        asset={asset}
        assetGQL={assetGQL}
        brand={brand}
        isOpen={isMidStayModalVisible}
        onRequestClose={() => {
          setMidStayModalVisible(false);
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "LodgingBusiness",
              name: title,
              description: "",
              address: {
                "@type": "PostalAddress",
                addressCountry: country,
                addressLocality: city,
                addressRegion: province,
                postalCode: postalCode,
                streetAddress: address,
              },
              image: featuredImage?.node.sourceUrl,
              checkinTime: "15:00",
              checkoutTime: "12:00",
              petsAllowed: true,
            }),
          }}
        />
      </Head>
    </>
  );
};

const useCarousel = () => {
  const [carouselVisible, setCarouselVisible] = useState<boolean>(false);
  const openCarousel = () => {
    document.body.classList.add(styles.carouselOpenBody);
    setCarouselVisible(true);
  };
  const closeCarousel = () => {
    document.body.classList.remove(styles.carouselOpenBody);
    setCarouselVisible(false);
  };

  useEffect(() => {
    const closeCarouselOnEscapePressed = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCarousel();
      }
    };

    window.addEventListener("keydown", closeCarouselOnEscapePressed);

    return () => {
      document.body.classList.remove(styles.carouselOpenBody);
      window.removeEventListener("keydown", closeCarouselOnEscapePressed);
    };
  }, []);

  return {
    isVisible: carouselVisible,
    open: openCarousel,
    close: closeCarousel,
  };
};

const HowToArrive: React.FC<{ icon: React.ReactNode; title: string }> = ({
  icon,
  title,
  children,
}) => (
  <div className={styles.howToArrive}>
    <div className={styles.howToArriveHeader}>
      {icon}
      <h3>{title}</h3>
    </div>
    <p>{children}</p>
  </div>
);

const PrivacyLink: React.FC<{ privacyPolicyUrl: string }> = ({
  children,
  privacyPolicyUrl,
}) => {
  return (
    <a href={privacyPolicyUrl} target="_blank">
      <Link>{children}</Link>
    </a>
  );
};

const MidStayForm: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
  brand: Brand;
  asset: AssetResponse;
  assetGQL: AssetIndexQuery["asset"];
}> = ({ asset, assetGQL, brand, isOpen, onRequestClose }) => {
  const { t } = useTranslation("asset");
  const lang = useLanguage();
  const { inputProps, handleSubmit } = useForm<MidStayFormProps>({
    translationContext: "asset:midStay.form.",
  });
  const [formSubmitResult, setFormSubmitResult] = useState<
    null | "success" | "error"
  >(null);

  const onSubmit = handleSubmit(async (data) => {
    const client = customerIoClient({
      formId: `mid-stay-form`,
      siteId: brand.crm?.customerIoSiteId || "",
    });
    try {
      await client.submitForm(
        {
          ...data,
          language: lang,
          asset_interest: asset.properties.code,
          business_segment: "Mid-Long Stay",
        },
        ["language", "asset_interest", "business_segment"],
      );
      setFormSubmitResult("success");
      window.dataLayer.push({
        event: "submit",
        event_category: "mid_stay",
        event_label: "form",
      });
    } catch (e) {
      captureError(e);
      setFormSubmitResult("error");
    }
  });

  return (
    <ActionSheet
      title={t("midStay.form.title")}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      actions={
        !formSubmitResult && (
          <Button form={"mid-stay-form"}>{t("midStay.form.submit")}</Button>
        )
      }
    >
      {!formSubmitResult && (
        <>
          {t("midStay.form.description")}

          <form
            className={styles.midStayForm}
            id="mid-stay-form"
            onSubmit={onSubmit}
          >
            <FormInput {...inputProps("first_name", { required: true })} />
            <FormInput {...inputProps("last_name", { required: true })} />
            <FormInput
              {...inputProps("email", {
                required: true,
                validate: { email },
              })}
            />
            <FormInput {...inputProps("phone", { required: true })} />
            <FormInputCheckbox
              {...inputProps("privacy_policy", {
                required: true,
                label: (
                  <Trans
                    i18nKey="asset:midStay.form.privacy_policy"
                    components={[
                      <PrivacyLink
                        privacyPolicyUrl={assetGQL?.privacyPolicyUrl || ""}
                      />,
                    ]}
                    values={{ brand: brand.name }}
                  />
                ),
              })}
            />
          </form>
        </>
      )}
      {formSubmitResult && (
        <Alert type={formSubmitResult}>
          {formSubmitResult === "success"
            ? t("midStay.form.success")
            : t("common:validation.unknownError")}
        </Alert>
      )}
    </ActionSheet>
  );
};

type MidStayFormProps = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  privacy_policy: boolean;
};

export const getServerSideProps = withDirectChannelCommonProps(
  async ({ req, locale, query }) => {
    const brand = req.context.brand;
    if (
      !locale ||
      !brand.locales.includes(locale || "") ||
      !query.assetType ||
      !query.slug ||
      typeof query.slug !== "string"
    ) {
      return {
        notFound: true,
      };
    }

    const response = await fetchCms(
      `{
        asset(id: "/${locale}/${query.assetType}/${query.slug}", idType: URI) {
          id
          uri
          slug
          title
          fullName
          content
          featuredImage {
            node {
              sourceUrl
              srcSet
            }
          }
          destination {
            destination {
              ... on Destination {
                title
                uri
                about {
                  images {
                    above { srcSet }
                    below { srcSet }
                  }
                }
                assets {
                  fullName
                  title,
                  uri
                }
              }
            }
          }
          properties {
            id
            code
            type
            stayType
            claim
            registrationNumber
            closed
            features {
              location
              privateParking
              bikeParking
              reducedMobility
              offlineReception
              laundryAndIroning
              swimmingPool
              lockers
              kitchen
              tv
              barRestaurant
              barRestaurantOpening
              commonTerrace
              hairDryer
              luggageStorage
              luggageLockers
            }
            location {
              address
              postalCode
              city
              province
              country
            }
            howToArrive {
              airport
              car
              bus
              metro
              taxi
              tram
              train
            }
            faq {
              answer
              question
            }
            geographicPosition {
              latitude
              longitude
            }
            travelGuide {
              image {
                srcSet
              }
              content
              title
            }
            gallery {
              sourceUrl
            }
          }
          translations {
            uri
            language {
              slug
            }
          }
          seo {
            metaDesc
          }
        }
      }`,
      req.context.brand.cmsUrl,
    );

    if (!response || !response.asset) {
      return {
        notFound: true,
      };
    }

    const asset = response.asset as AssetResponse;

    const { asset: assetGQL, loyaltyProgram } = await ssrFetcher<
      AssetIndexQuery,
      AssetIndexQueryVariables
    >(req, locale, AssetIndexDocument, { assetId: asset.properties.id })();

    const t = await useTranslationServer(locale, "common");

    return {
      props: {
        ...(await serverSideTranslations("/asset/[slug]", locale)),
        asset,
        assetGQL,
        isViewerInCommunity: !!loyaltyProgram,
        mapsApiKey: process.env.MAPS_API_KEY || "",
        trackingContext: {
          page_type: "asset_home",
          asset_id: asset.properties.id || "",
          attempt_id: nanoid(),
        },
        languageAlternates: [
          {
            hrefLang: locale,
            href: asset.uri,
          },
          ...asset.translations.reduce(
            (prev: LanguageAlternates, translation) => {
              if (!translation.uri || !translation.language.slug) {
                return prev;
              }
              return [
                ...prev,
                { hrefLang: translation.language.slug, href: translation.uri },
              ];
            },
            [],
          ),
        ],
        breadcrumbs: [
          { name: t("common:breadcrumbs.home"), item: `/${locale}` },
          ...(brand.enabledFeatures?.includes("destinations") &&
          asset.destination.destination
            ? [
                {
                  name: t("common:breadcrumbs.destination", {
                    city: asset.destination.destination.title,
                  }),
                  item: asset.destination.destination.uri,
                },
              ]
            : []),
          {
            name: asset.fullName,
            item: asset.uri,
          },
        ],
        page: {
          searchBar: {
            assets: brand.enabledFeatures?.includes("destinations")
              ? (asset.destination.destination?.assets || []).map((a) => ({
                  title: a.title,
                  uri: `${a.uri}#spaces`,
                }))
              : [
                  {
                    title: asset.title,
                    uri: `${asset.uri}#spaces`,
                  },
                ],
            optionsDefault: `${asset.uri}#spaces`,
          },
          isRootPage: true,
        },
      },
    };
  },
);

type AssetProps = {
  asset: AssetResponse;
  assetGQL: AssetIndexQuery["asset"];
  mapsApiKey: string;
  isViewerInCommunity: boolean;
};

type AssetResponse = {
  uri: string;
  slug: string;
  title: string;
  fullName: string;
  content: string;
  featuredImage?: { node: { sourceUrl: string; srcSet: string } };
  destination: {
    destination?: {
      title: string;
      uri: string;
      about: {
        images: {
          above: {
            srcSet: string;
          };
          below: {
            srcSet: string;
          };
        };
      };
      assets: Array<{ title: string; uri: string }>;
    };
  };
  properties: {
    id: string;
    code: string;
    type: string;
    stayType: string;
    claim: string;
    registrationNumber: string;
    closed: boolean;
    features: {
      location: string;
      privateParking: boolean;
      bikeParking: boolean;
      reducedMobility: boolean;
      offlineReception: boolean;
      laundryAndIroning: boolean;
      swimmingPool: boolean;
      lockers: "no" | "lock_required" | "no_lock_required";
      kitchen: "no" | "private" | "shared" | "mixed";
      tv: "in_the_bedroom" | "common_areas" | "no" | null;
      barRestaurant: boolean;
      barRestaurantOpening: string;
      luggageStorage: boolean;
      luggageLockers: boolean;
      commonTerrace: boolean;
      hairDryer: "no" | "yes" | "under_request" | null;
    };
    location: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
    };
    howToArrive: {
      airport?: string;
      car?: string;
      bus?: string;
      metro?: string;
      taxi?: string;
      tram?: string;
      train?: string;
    };
    faq: Array<{ question: string; answer: string }>;
    geographicPosition: { latitude: number; longitude: number };
    travelGuide: Array<{
      title: string;
      content: string;
      image: { srcSet: string };
    }>;
    gallery: Array<{ sourceUrl: string }>;
  };
  translations: Array<{ uri: string; language: { slug: string } }>;
  seo: { metaDesc: string };
};

export default Asset;
